<template>
  <div id="app">
    <div v-if="browserIsFirefox" class="esg-warning">
      <p class="esg-warning--heading">Hello, Firefox user...</p>
      <p>
        You have excellent taste in browsers! Unfortunately there are a few bugs in Firefox which cause issues when
        copying your signature to Gmail or Outlook. Please use Chrome, just this once.
      </p>
    </div>

    <template v-if="!isSignedIn && googleApiReady">
      <button v-on:click="signIn" class="esg-button" style="margin: 0 auto; display: block;">Sign in with Google</button>
    </template>
    <template v-else-if="isSignedIn && googleApiReady">
      <h1>Email signature generator</h1>
      <a href="#" v-on:click.prevent="signOut" class="esg-link esg-link--sign-out">Sign out</a>

      <h2>1. Your details</h2>
      <div class="esg-field">
        <label class="esg-field__label" for="name">Name</label>
        <input class="esg-field__input" id="name" autocomplete="name" v-model="personDetails.name" aria-required="true" required>
      </div>
      <div class="esg-field">
        <label class="esg-field__label" for="job-title">Job title</label>
        <input class="esg-field__input" id="job-title" autocomplete="organization-title" v-model="personDetails.jobTitle" aria-required="true" required>
      </div>
      <div class="esg-field">
        <label class="esg-field__label" for="email">Email address</label>
        <input class="esg-field__input" type="email" id="email" autocomplete="email"  v-model="personDetails.email" aria-required="true" required>
      </div>
      <div class="esg-field">
        <label class="esg-field__label" for="extension">Phone extension (optional)</label>
        <input class="esg-field__input" id="extension" autocomplete="tel-extension" v-model="personDetails.extension" aria-required="false">
      </div>
      <div class="esg-field">
        <label class="esg-field__label" for="mobile">Mobile (optional)</label>
        <input class="esg-field__input" id="mobile" autocomplete="tel" v-model="personDetails.mobile" aria-required="false" placeholder="Include a space, eg 07700 900123">
      </div>
      <div class="esg-field">
        <label class="esg-field__label" for="extra-info">Extra info (optional)</label>
        <textarea class="esg-field__input esg-field__input--textarea" id="extra-info" v-model="personDetails.extraInfo" aria-required="false" placeholder="eg working hours"></textarea>
      </div>

      <h2>2. Signature options</h2>

      <div class="esg-field">
        <label class="esg-field__label" for="email-app">Which email app do you use?</label>
        <select class="esg-field__input" id="email-app" v-model="personDetails.emailApp" aria-required="true">
          <option id="none-email-app" value="" disabled></option>
          <option id="gmail" value="gmail">Gmail</option>
          <option id="outlook" value="outlook">Outlook</option>
        </select>
      </div>

      <details>
        <summary class="esg-summary">I use both Outlook and Gmail</summary>
        <p>
          You'll need to create different signatures for each app.
          They'll look the same, but the underlying code is different, so a Gmail signature won't work properly in Outlook and vice-versa.
          Come back and change this option when you're finished to create your signature for the other app.
        </p>
      </details>

      <div class="esg-field">
        <label class="esg-field__label" for="brand">Which brand should your signature use?</label>
        <select class="esg-field__input" id="brand" v-model="personDetails.brand" aria-required="true">
          <option id="none-brand" value="" disabled></option>
          <option v-for="brand in brands" :id="brand.id" :value="brand.id" :key="brand.id">{{ brand.name }}</option>
        </select>
      </div>

      <details>
        <summary class="esg-summary">Which brand should I use?</summary>
        <p>
          If you're on the eCase or Fivium Digital teams, you should usually use the appropriate brand for your team.
        </p>
        <p>
          If you work across both eCase and Fivium Digital projects, such as ops or marketing, you should set up a signature for
          each brand and choose the appropriate signature based on the customer you're emailing. You can use the Fivium
          brand for internal emails. There's guidance later on to help you set up multiple signatures.
        </p>
        <p>
          If you don't work on Fivium Digital or eCase projects, such as the people team, you should use the Fivium brand.
        </p>
      </details>

      <template v-if="currentBrandHasCta">
        <div class="esg-field">
          <label class="esg-field__label" for="showCta">Should the marketing message be shown?</label>
          <select class="esg-field__input" id="showCta" v-model="personDetails.showCta" aria-required="true">
            <option id="showCta-true" :value="true">Yes</option>
            <option id="showCta-false" :value="false">No</option>
          </select>
        </div>

        <details>
          <summary class="esg-summary">Should I show the marketing message?</summary>
          <p>
            We use the marketing message to highlight things we want our customers to see, like new case studies or
            events we're hosting.
          </p>
          <p>
            We update the marketing message regularly. If you're not in a customer-facing role and you don't want to
            change your signature regularly, you can keep it hidden. If you're customer-facing (for example, sales or customer
            success) you should show it. The marketing team will let you know whenever we change it so you can update
            your signature using this tool.
          </p>
        </details>
      </template>

      <template v-if="mandatoryDetailsComplete">
        <h2>3. Preview your signature</h2>
        <div class="esg-preview">
          <div class="esg-template" ref="signature">
            <signature-template-fivium-digital v-if="personDetails.brand === 'fiviumDigital'"
                                             :name="personDetails.name"
                                             :email="personDetails.email"
                                             :job-title="personDetails.jobTitle"
                                             :extension="personDetails.extension"
                                             :mobile="personDetails.mobile"
                                             :extra-info="personDetails.extraInfo"
                                             :email-app="personDetails.emailApp"
                                             :cta-text="currentBrand.cta.text"
                                             :cta-link-text="currentBrand.cta.linkText"
                                             :cta-link-url="currentBrand.cta.linkUrl"
                                             :show-cta="personDetails.showCta"
            />
            <signature-template-ecase v-if="personDetails.brand === 'ecase'"
                                               :name="personDetails.name"
                                               :email="personDetails.email"
                                               :job-title="personDetails.jobTitle"
                                               :extension="personDetails.extension"
                                               :mobile="personDetails.mobile"
                                               :extra-info="personDetails.extraInfo"
                                               :email-app="personDetails.emailApp"
                                               :cta-text="currentBrand.cta.text"
                                               :cta-link-text="currentBrand.cta.linkText"
                                               :cta-link-url="currentBrand.cta.linkUrl"
                                               :show-cta="personDetails.showCta"
            />
            <signature-template-fivium v-if="personDetails.brand === 'fivium'"
                                      :name="personDetails.name"
                                      :email="personDetails.email"
                                      :job-title="personDetails.jobTitle"
                                      :extension="personDetails.extension"
                                      :mobile="personDetails.mobile"
                                      :extra-info="personDetails.extraInfo"
                                      :email-app="personDetails.emailApp"
                                      :cta-text="currentBrand.cta.text"
                                      :cta-link-text="currentBrand.cta.linkText"
                                      :cta-link-url="currentBrand.cta.linkUrl"
                                      :show-cta="personDetails.showCta"
            />
          </div>
        </div>

        <h2>4. Set your signature</h2>

        <fieldset class="esg-fieldset">
          <legend class="esg-legend">Do you send email under multiple brands?</legend>
          <div class="esg-yes-no">
            <input type="radio" id="uses-multiple-brands-yes" :value="true" v-model="personDetails.usesMultipleBrands">
            <label for="uses-multiple-brands-yes">Yes, I also use the {{ nonCurrentBrandsDisplay}} brands</label>
          </div>
          <div class="esg-yes-no">
            <input type="radio" id="uses-multiple-brands-no" :value="false" v-model="personDetails.usesMultipleBrands">
            <label for="uses-multiple-brands-no">No, I only send email with the {{ currentBrandDisplay }} brand</label>
          </div>
        </fieldset>

        <template v-if="personDetails.usesMultipleBrands !== null">

          <ol class="esg-ordered-list">
          <template v-if="personDetails.emailApp === 'gmail'">
            <li>Open your Gmail settings in a new tab with this handy link: <a href="https://mail.google.com/mail/u/0/#settings/general" class="esg-link" target="_blank">Gmail settings</a></li>
            <li>Scroll down to the Signature section near the bottom.</li>
          </template>

          <template v-if="personDetails.emailApp === 'outlook'">
            <li>
              Click 'New Email' in the top-left of Outlook.
              <img src="./assets/img/outlook-new-email.png" alt="" class="esg-guidance-image">
            </li>

            <li>
              Click the 'Signatures' button in the toolbar, then select 'Signatures...'
              <img src="./assets/img/outlook-signature-dropdown.png" alt="" class="esg-guidance-image">
            </li>
          </template>

          <template v-if="personDetails.usesMultipleBrands && personDetails.emailApp === 'gmail'">
            <li>
              Delete any signatures you didn't create using this generator by clicking the bin icon next to them, then the blue
              'Delete' button in the popup that appears.
              <img src="./assets/img/gmail-delete-existing.png" alt="" class="esg-guidance-image">
            </li>

            <li>
              Click the 'Create new' button, and name your signature '{{ currentBrandDisplay }}'
              <img src="./assets/img/gmail-signature-name.png" alt="" class="esg-guidance-image">
            </li>

            <li>
              Click this button to copy your signature:
              <button class="esg-button" v-on:click="copySignatureToClipboard">
                {{ copyButtonText }}
              </button>
            </li>

            <li>Go back to your Gmail settings. Click inside the signature editor on the right, then press Ctrl+V (or Command+V on Mac) to paste your signature.</li>
            <li>
              If {{ currentBrandDisplay }} is the brand you use most often, under 'Signature defaults' select
              '{{ currentBrandDisplay }}' in both dropdowns.
            </li>
            <li>
              Tick 'Insert signature before quoted text in replies and remove the "--" line that precedes it.'
              <img src="./assets/img/gmail-remove-dashes.png" alt="" class="esg-guidance-image">
            </li>

            <li>Scroll to the bottom of the page and click 'Save Changes'.</li>

            <li>
              <a href="#brand" class="esg-link">Go back to the brand dropdown</a> on this page and repeat for each brand you use.
            </li>
          </template>
          <template v-if="!personDetails.usesMultipleBrands && personDetails.emailApp === 'gmail'">
            <li>
              Delete your existing signature by clicking the bin icon, then the blue 'Delete' button in the popup that appears.
              <img src="./assets/img/gmail-delete-existing.png" alt="" class="esg-guidance-image">
            </li>

            <li>
              Click the 'Create new' button, and name your signature '{{ currentBrandDisplay }}'
              <img src="./assets/img/gmail-signature-name.png" alt="" class="esg-guidance-image">
            </li>

            <li>
              Click this button to copy your signature:
              <button class="esg-button" v-on:click="copySignatureToClipboard">
                {{ copyButtonText }}
              </button>
            </li>

            <li>Go back to your Gmail settings. Click inside the signature editor on the right, then press Ctrl+V (or Command+V on Mac) to paste your signature.</li>
            <li>Under 'Signature defaults', select '{{ currentBrandDisplay }}' in both dropdowns</li>
            <li>
              Tick 'Insert signature before quoted text in replies and remove the "--" line that precedes it.'
              <img src="./assets/img/gmail-remove-dashes.png" alt="" class="esg-guidance-image">
            </li>

            <li>Scroll to the bottom of the page and click 'Save Changes'.</li>
          </template>

          <template v-if="personDetails.usesMultipleBrands && personDetails.emailApp === 'outlook'">
            <li>
              Delete any signatures you didn't create using this generator by clicking the name of the signature then the 'Delete' button.
              <img src="./assets/img/outlook-delete-existing.png" alt="" class="esg-guidance-image">
            </li>

            <li>
              Click the 'New' button, and name your signature '{{ currentBrandDisplay }}'
              <img src="./assets/img/outlook-signature-name.png" alt="" class="esg-guidance-image">
            </li>

            <li>
              Click this button to copy your signature:
              <button class="esg-button" v-on:click="copySignatureToClipboard">
                {{ copyButtonText }}
              </button>
            </li>

            <li>Go back to Outlook. Click inside the 'Edit signature' box at the bottom, then press Ctrl+V (or Command+V on Mac) to paste your signature.</li>
            <li>
              If {{ currentBrandDisplay }} is the brand you use most often, under 'Choose default signature' select
              '{{ currentBrandDisplay }}' in the bottom two dropdowns.
            </li>
            <li>Click the 'OK' button.</li>
            <li>
              <a href="#brand" class="esg-link">Go back to the brand dropdown</a> on this page and repeat for each brand you use.
            </li>
          </template>
          <template v-if="!personDetails.usesMultipleBrands && personDetails.emailApp === 'outlook'">
            <li>
              Delete your existing signature by clicking the name of the signature then the 'Delete' button.
              <img src="./assets/img/outlook-delete-existing.png" alt="" class="esg-guidance-image">
            </li>

            <li>
              Click the 'New' button, and name your signature '{{ currentBrandDisplay }}'
              <img src="./assets/img/outlook-signature-name.png" alt="" class="esg-guidance-image">
            </li>

            <li>
              Click this button to copy your signature:
              <button class="esg-button" v-on:click="copySignatureToClipboard">
                {{ copyButtonText }}
              </button>
            </li>
            <li>Go back to Outlook. Click inside the 'Edit signature' box at the bottom, then press Ctrl+V (or Command+V on Mac) to paste your signature.</li>
            <li>Under 'Choose default signature', select '{{ currentBrandDisplay }}' in the bottom two dropdowns</li>
            <li>Click the 'OK' button.</li>
          </template>
          </ol>
        </template>

        <template v-if="personDetails.usesMultipleBrands !== null">
          <h2>5. Use your signature</h2>
          <template v-if="personDetails.usesMultipleBrands && personDetails.emailApp === 'gmail'">
            <p>When you send an email, you can change your signature using the pen icon at the bottom of the 'New Message' panel:</p>
            <img src="./assets/img/gmail-select-signature.png" alt="" class="esg-guidance-image">
            <p>Send yourself a test email with each signature to check everything looks okay.</p>
          </template>
          <template v-if="!personDetails.usesMultipleBrands && personDetails.emailApp === 'gmail'">
            <p>When you send an email, your signature will appear automatically.</p>
            <p>Send yourself a test email to check everything looks okay.</p>
          </template>

          <template v-if="personDetails.usesMultipleBrands && personDetails.emailApp === 'outlook'">
            <p>When you send an email, you can change your signature using the pen icon at the top of the window:</p>
            <img src="./assets/img/outlook-select-signature.png" alt="" class="esg-guidance-image">
            <p>Send yourself a test email with each signature to check everything looks okay.</p>
          </template>
          <template v-if="!personDetails.usesMultipleBrands && personDetails.emailApp === 'outlook'">
            <p>When you send an email, your signature will appear automatically.</p>
            <p>Send yourself a test email to check everything looks okay.</p>
          </template>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
import SignatureTemplateFiviumDigital from './components/SignatureTemplateFiviumDigital.vue';
import SignatureTemplateEcase from './components/SignatureTemplateEcase.vue';
import SignatureTemplateFivium from './components/SignatureTemplateFivium.vue';

export default {
  name: 'App',
  components: {
    SignatureTemplateFiviumDigital,
    SignatureTemplateEcase,
    SignatureTemplateFivium
  },
  data() {
    return {
      googleApi: null,
      googleApiReady: false,
      isSignedIn: false,
      copyButtonText: 'Copy to clipboard',
      personDetails: {
        name: null,
        jobTitle: null,
        email: null,
        extension: null,
        mobile: null,
        extraInfo: null,
        emailApp: '',
        brand: '',
        usesMultipleBrands: null,
        showCta: false
      },
      brands: [
        {
          id: 'fivium',
          name: 'Fivium',
          cta: {
            text: null,
            linkText: null,
            linkUrl: null
          }
        },
        {
          id: 'fiviumDigital',
          name: 'Fivium Digital',
          cta: {
            text: 'Find out how Fivium Digital helped the Adoption Support Fund make application<br>submissions easier and more efficient. ',
            linkText: 'Read the case study',
            linkUrl: 'https://www.fivium.co.uk/application/files/3415/8392/3444/asf-web.pdf'
          }
        },
        {
          id: 'ecase',
          name: 'eCase',
          cta: {
            text: 'eCase FOI Awards 2027: ',
            linkText: 'Nominate today',
            linkUrl: 'https://ecasefoiawards.co.uk'
          }
        },
      ]
    }
  },
  watch: {
    personDetails: {
      deep: true,
      handler() {
        localStorage.setItem('SigGeneratorPersonDetails', JSON.stringify(this.personDetails));
      }
    },
    isSignedIn() {
      if(this.isSignedIn) {
        const user = this.googleApi.auth2.getAuthInstance().currentUser.get().getBasicProfile();

        if(!this.personDetails.name) {
          this.personDetails.name = user.getName();
        }

        if(!this.personDetails.email) {
          this.personDetails.email = user.getEmail();
        }
      }
    }
  },
  computed: {
    currentBrand() {
      return this.brands.filter(b => b.id === this.personDetails.brand)[0];
    },
    currentBrandDisplay() {
      return (this.currentBrand) ? this.currentBrand.name : null;
    },
    currentBrandHasCta() {
      return this.currentBrand && (this.currentBrand.cta.text || (this.currentBrand.cta.linkText && this.currentBrand.cta.linkUrl));
    },
    nonCurrentBrandsDisplay() {
      return this.brands.filter(b => b.id != this.personDetails.brand).map(b => b.name).join(' or ');
    },
    mandatoryDetailsComplete() {
      return this.personDetails.name && this.personDetails.jobTitle && this.personDetails.email && this.personDetails.brand && this.personDetails.emailApp;
    },
    browserIsFirefox() {
      return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    }
  },
  mounted() {
    const gApiScript = document.createElement('script');
    gApiScript.setAttribute('src', 'https://apis.google.com/js/api:client.js');
    gApiScript.addEventListener('load', () => {
      window.gapi.load('auth2', this.initGoogleClient);
      this.googleApi = window.gapi;
    });
    document.head.appendChild(gApiScript);

    const storedPersonDetails = localStorage.getItem('SigGeneratorPersonDetails');

    if(storedPersonDetails !== null) {
      const parsedDetails = JSON.parse(storedPersonDetails);
      this.personDetails.name = parsedDetails.name;
      this.personDetails.jobTitle = parsedDetails.jobTitle;
      this.personDetails.email = parsedDetails.email;
      this.personDetails.extension = parsedDetails.extension;
      this.personDetails.mobile = parsedDetails.mobile;
      this.personDetails.extraInfo = parsedDetails.extraInfo;
      this.personDetails.emailApp = parsedDetails.emailApp;
      this.personDetails.brand = parsedDetails.brand;
      this.personDetails.usesMultipleBrands = parsedDetails.usesMultipleBrands;

      // eslint-disable-next-line no-prototype-builtins
      if(parsedDetails.hasOwnProperty('showCta')) {
        this.personDetails.showCta = parsedDetails.showCta
      }
    }
  },
  methods: {
    copyElementToClipboard(element) {
      window.getSelection().removeAllRanges();
      let range = document.createRange();
      range.selectNode(typeof element === 'string' ? document.getElementById(element) : element);
      window.getSelection().addRange(range);
      document.execCommand('copy');
      window.getSelection().removeAllRanges();
    },
    copySignatureToClipboard() {
      this.copyElementToClipboard(this.$refs.signature);
      this.copyButtonText = 'Copied!'
      setTimeout(() => this.copyButtonText = 'Copy to clipboard', 5000);
    },
    initGoogleClient() {
      this.googleApi.auth2.init({
        client_id: '68840449495-ofq4a7oafp8s89f6e8vs7rodtpsu3733.apps.googleusercontent.com',
        cookiepolicy: 'single_host_origin'
      }).then(() => {
        this.googleApiReady = true;
        // Listen for sign-in state changes.
        this.googleApi.auth2.getAuthInstance().isSignedIn.listen(this.updateSigninStatus);

        // Handle the initial sign-in state.
        this.updateSigninStatus(this.googleApi.auth2.getAuthInstance().isSignedIn.get());
      }, function(error) {
        console.log(error);
      });
    },
    signIn() {
      this.googleApi.auth2.getAuthInstance().signIn();
    },
    updateSigninStatus(isSignedIn) {
      this.isSignedIn = isSignedIn;
    },
    signOut() {
      this.googleApi.auth2.getAuthInstance().signOut();
    }
  }
}
</script>

<style lang="scss">
@import 'assets/scss/reset';
@import url('https://fonts.googleapis.com/css2?family=Overpass:wght@400;700&display=swap');

$blue: #0070BA;

* {
  box-sizing: border-box;
}

td[valign=middle] {
  vertical-align: middle;
}

#app {
  max-width: 800px;
  margin: 3em auto;
  font-family: 'Overpass', sans-serif;
}

h1 {
  font-size: 2.5rem;
  letter-spacing: -0.06em;
  font-weight: 700;
  margin-bottom: 1em;
}

h2 {
  font-size: 1.7rem;
  letter-spacing: -0.06em;
  font-weight: 700;
  margin-bottom: 1em;
  padding-top: 2em;
}

.esg-field {
  margin-bottom: 1em;

  &__label {
    width: 30%;
    text-align: right;
    display: inline-block;
    font-weight: 700;
    padding: 0.5em 1em 0.3em 0.5em;
    vertical-align: top;
  }

  &__input {
    display: inline-block;
    width: 70%;
    max-width: 300px;
    height: 1.8em;
    font-family: 'Overpass', sans-serif;
    font-size: 1em;
    padding: 0.5em 0.5em 0.3em 0.5em;
    border: 1px solid #ccc;
    border-radius: 5px;

    &--textarea {
      height: 4em;
    }
  }

  &__radio-list {
    display: inline-block;
    width: 70%;
    max-width: 300px;
  }
}

.esg-preview {
  border: 1px solid #ccc;
  padding: 1em;
  border-radius: 5px;
  margin-bottom: 1em;
  line-height: initial;
}
.esg-template {
  font-family: Arial, Helvetica, sans-serif;

  b {
    font-weight: bold;
  }
}

.esg-button {
  border: 1px solid transparent;
  background: $blue;
  box-shadow: 0 2px 0 darken($blue, 20%);
  color: white;
  font-family: 'Overpass', sans-serif;
  font-size: 1em;
  padding: 0.5em 0.5em 0.3em 0.5em;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background: lighten($blue, 2%);
  }
}

p {
  margin-bottom: 1em;
  line-height: 1.5em;
}

.esg-fieldset {
  margin-bottom: 2em;
}

.esg-legend {
  font-weight: bold;
  margin-bottom: 0.5em;
}

.esg-summary {
  color: $blue;
  text-decoration: underline;
  padding: 0.5em 0;
  cursor: pointer;
  margin-bottom: 1em;
}

.esg-link {
  color: $blue;

  &--sign-out {
    float: right;
    margin-top: -4em;
  }
}

.esg-guidance-image {
  border: 1px solid #ccc;
  padding: 1em;
  border-radius: 5px;
  display: block;
  margin-bottom: 1em;
  margin-top: 1em;
}

.esg-yes-no {
  margin-bottom: 0.5em;
}

.esg-ordered-list {
  list-style-type: decimal;
  padding-left: 1em;

  li {
    margin-bottom: 2em;
    line-height: 1.5em;
  }
}

.esg-warning {
  padding: 1em;
  background: #fff1a8;
  margin-bottom: 2em;

  &--heading {
    font-weight: bold;
  }

  p {
    margin-bottom: 0;
  }
}
</style>
